import React, { useState } from 'react';
import { ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { company } from '../mock';
import { useToast } from '../hooks/use-toast';

const ContactCTA = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing details', description: 'Please complete the required fields.' });
      return;
    }
    toast({
      title: 'Request received',
      description: 'A senior partner from KMH will reach out within one business day.',
    });
    setForm({ name: '', email: '', company: '', message: '' });
  };

  return (
    <section id="contact" className="relative py-24 lg:py-36 bg-ink text-ivory overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, #4dc0c0 1px, transparent 1px), linear-gradient(to bottom, #4dc0c0 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="gold-divider"></span>
              <span className="font-mono-label text-gold-soft">Engage Our Practice</span>
            </div>
            <h2 className="font-serif-display text-4xl lg:text-[3.6rem] leading-[1.05]">
              Let’s convert your compliance into <span className="italic text-gold-soft">competitive advantage</span>.
            </h2>
            <p className="font-sans-body text-lg text-ivory/70 leading-relaxed mt-8 max-w-lg">
              Tell us about your business. A founding partner will personally review your enquiry and respond
              within one business day.
            </p>

            <div className="mt-12 space-y-7">
              <a href={`tel:${company.phone}`} className="flex items-center gap-5 group">
                <span className="w-14 h-14 rounded-full border border-gold/40 group-hover:bg-gold/15 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-gold-soft" strokeWidth={1.4} />
                </span>
                <div>
                  <p className="font-mono-label text-gold-soft">Call Direct</p>
                  <p className="font-serif-display text-2xl text-ivory mt-1">{company.phone}</p>
                </div>
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-5 group">
                <span className="w-14 h-14 rounded-full border border-gold/40 group-hover:bg-gold/15 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-gold-soft" strokeWidth={1.4} />
                </span>
                <div>
                  <p className="font-mono-label text-gold-soft">Write to Us</p>
                  <p className="font-serif-display text-2xl text-ivory mt-1">{company.email}</p>
                </div>
              </a>
              <div className="flex items-center gap-5">
                <span className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-gold-soft" strokeWidth={1.4} />
                </span>
                <div>
                  <p className="font-mono-label text-gold-soft">Studio</p>
                  <p className="font-serif-display text-2xl text-ivory mt-1">{company.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-5 lg:col-start-8">
            <form onSubmit={submit} className="bg-ivory text-ink p-8 lg:p-10 relative">
              <p className="font-mono-label text-gold-dark">Project Enquiry</p>
              <h3 className="font-serif-display text-3xl mt-3 mb-8">Begin a conversation.</h3>

              <div className="space-y-6">
                <div>
                  <label className="font-mono-label text-muted-kmh">Full Name *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border-b border-line bg-transparent py-3 mt-2 font-sans-body focus:border-gold focus:outline-none"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="font-mono-label text-muted-kmh">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border-b border-line bg-transparent py-3 mt-2 font-sans-body focus:border-gold focus:outline-none"
                    placeholder="jane@company.com"
                  />
                </div>
                <div>
                  <label className="font-mono-label text-muted-kmh">Company</label>
                  <input
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full border-b border-line bg-transparent py-3 mt-2 font-sans-body focus:border-gold focus:outline-none"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="font-mono-label text-muted-kmh">How can we help? *</label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full border-b border-line bg-transparent py-3 mt-2 font-sans-body focus:border-gold focus:outline-none resize-none"
                    placeholder="Tell us about your compliance landscape..."
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn-premium relative inline-flex items-center gap-3 mt-10 bg-ink text-ivory hover:text-ivory px-8 py-4 font-mono-label w-full justify-center"
                style={{ background: 'var(--kmh-ink)' }}
              >
                <span className="relative z-10">Send Enquiry</span>
                <ArrowRight className="w-4 h-4 relative z-10" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;
